import { Tooltip } from 'antd'
import { TooltipPlacement } from 'antd/es/tooltip'
import {
  EXPLORER_BORDER_GRAY_LOW_OPACITY,
  EXPLORER_INPUT_GRAY,
} from 'features/drilldown/colors'
import _ from 'lodash'
import { FC, ReactNode } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

import { formatValueForDisplay } from './utils'

const PopupContainer = styled.div`
  width: 550px;
  height: 450px;
  overflow-y: auto;
  padding: 1rem;
  background: ${EXPLORER_INPUT_GRAY};
  border: 0.5px solid ${EXPLORER_BORDER_GRAY_LOW_OPACITY};
`

const PropertyRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 12px;
`

const PropertyKey = styled.span`
  font-weight: 600;
  color: ${CantileverTheme.colors.gray['400']};
  margin-right: 8px;
`

const PropertyValue = styled.span`
  color: ${CantileverTheme.colors.gray['400']};
  word-break: break-word;
  text-align: right;
  flex: 1;
`

const EmptyState = styled.div`
  color: ${CantileverTheme.colors.gray['400']};
  font-style: italic;
`

const HoverableContent = styled.span`
  cursor: help;
`

interface EntryDataPopupProps {
  entryData: Record<string, unknown>
  children?: ReactNode
  placement?: TooltipPlacement
}

/**
 * Renders a popup component that displays all properties of an entry in a formatted way.
 * Can be used as a standalone component or as a tooltip wrapper.
 *
 * As a follow-up feature, we could apply the property value renderers here by passing in the correct type.

 */
const renderPropertyValue = (value: unknown): string => {
  if (_.isNil(value)) {
    return '—'
  }

  // Use shared formatting logic for consistent display
  return formatValueForDisplay(value, 'text')
}

export const EntryPropertiesView: FC<{
  entryData: Record<string, unknown>
}> = ({ entryData }) => {
  const properties = Object.entries(entryData).sort(([a], [b]) =>
    a.localeCompare(b)
  )

  return (
    <PopupContainer>
      {properties.length === 0 ? (
        <EmptyState>No properties available</EmptyState>
      ) : (
        properties.map(([key, value]) => (
          <PropertyRow key={key}>
            <PropertyKey>{key}:</PropertyKey>
            <PropertyValue>{renderPropertyValue(value)}</PropertyValue>
          </PropertyRow>
        ))
      )}
    </PopupContainer>
  )
}

export const EntryPropertiesPopup: FC<EntryDataPopupProps> = ({
  entryData,
  children,
  placement = 'right',
}) => {
  const entryPropertiesView = <EntryPropertiesView entryData={entryData} />

  return (
    <Tooltip
      title={entryPropertiesView}
      placement={placement}
      styles={{ root: { maxWidth: 'none' } }}>
      <HoverableContent>{children}</HoverableContent>
    </Tooltip>
  )
}
